
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { EditorHeader } from "./EditorHeader";

export const TeamEditor = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: teamMembers, isLoading } = useQuery({
    queryKey: ['team-members'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .order('created_at');
      
      if (error) throw error;
      return data;
    },
  });

  const handleSave = async (e: React.FormEvent<HTMLFormElement>, memberId: string) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const updates = {
      name: String(formData.get('name')),
      role: String(formData.get('role')),
      bio: String(formData.get('bio')),
      experience: String(formData.get('experience')),
      avatar_url: String(formData.get('avatar_url')),
      responsibilities: String(formData.get('responsibilities')).split(',').map(r => r.trim()),
      linkedin: String(formData.get('linkedin')),
    };

    try {
      const { error } = await supabase
        .from('team_members')
        .update(updates)
        .eq('id', memberId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Team member has been updated",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update team member",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdd = async () => {
    try {
      const { error } = await supabase
        .from('team_members')
        .insert({
          name: 'New Team Member',
          role: 'Role',
          bio: 'Bio',
          experience: 'Experience',
          responsibilities: [],
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "New team member has been added",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add team member",
      });
    }
  };

  const handleAddMamduh = async () => {
    try {
      const { error } = await supabase
        .from('team_members')
        .insert({
          name: 'Mamduh Allam',
          role: 'Area Vice President Saudi Arabia, Bahrain, and Kuwait at Splunk',
          bio: 'Strategic technology leader with extensive experience in driving business growth and digital transformation across enterprise and government sectors in the Middle East.',
          experience: 'Over 15 years of progressive leadership experience across technology giants including Splunk, Microsoft, F5, and Cisco. Expert in telecommunications, media, and enterprise technology solutions with a proven track record of delivering multi-million dollar operations and achieving 100% of forecasted targets.',
          avatar_url: '/lovable-uploads/f8c7d2bb-ea55-4b34-bba2-9ca8cd238453.png',
          responsibilities: [
            'Strategic leadership for technology sector advisory',
            'Enterprise and government digital transformation',
            'Business development and operational excellence',
            'Team management and customer satisfaction',
            'Regional market expansion and growth strategies'
          ],
          linkedin: null,
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Mamduh Allam has been added to the team",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add Mamduh Allam",
      });
    }
  };

  const handleDelete = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Team member has been deleted",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete team member",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditorHeader title="Team Members Editor" />
      <div className="flex gap-4 mb-4">
        <Button onClick={handleAdd}>
          <Plus className="h-4 w-4 mr-2" />
          Add Team Member
        </Button>
        <Button onClick={handleAddMamduh} variant="outline">
          <Plus className="h-4 w-4 mr-2" />
          Add Mamduh Allam
        </Button>
      </div>

      <div className="space-y-6">
        {teamMembers?.map((member) => (
          <Card key={member.id}>
            <CardContent className="pt-6">
              <form onSubmit={(e) => handleSave(e, member.id)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Name</label>
                    <Input
                      name="name"
                      defaultValue={member.name}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Role</label>
                    <Input
                      name="role"
                      defaultValue={member.role}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Bio</label>
                  <Textarea
                    name="bio"
                    defaultValue={member.bio}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Experience</label>
                  <Textarea
                    name="experience"
                    defaultValue={member.experience}
                    className="mt-1"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Avatar URL</label>
                  <Input
                    name="avatar_url"
                    defaultValue={member.avatar_url || ''}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">LinkedIn URL</label>
                  <Input
                    name="linkedin"
                    defaultValue={member.linkedin || ''}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Responsibilities (comma-separated)</label>
                  <Input
                    name="responsibilities"
                    defaultValue={member.responsibilities.join(', ')}
                    className="mt-1"
                  />
                </div>
                <div className="flex justify-between">
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                  <Button 
                    type="button"
                    variant="destructive"
                    onClick={() => handleDelete(member.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
